"use client"

import { TableCell, TableRow } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { CheckCircle, XCircle, EyeOff, Eye, FileText } from "lucide-react"
import { NoteCell } from "./note-cell"
import type { UseFormReturn } from "react-hook-form"
import { ProcessedApplicant } from "@/utils/applicantsTable/customTypes"
import { Link } from "@tanstack/react-router"
import PresignedLink from "@/components/Tables/Candidates/ViewResume"

interface ApplicantRowProps {
  applicant: ProcessedApplicant
  hiringCampaignId: string
  isEditing: boolean
  form: UseFormReturn<{ note: string }>
  onEditNote: (userId: string, currentNote: string) => void
  onSaveNote: (userId: string) => void
  onCancelNote: () => void
  onToggleHidden: (userId: string, currentHiddenState: boolean) => void
}

export function ApplicantRow({
  applicant,
  hiringCampaignId,
  isEditing,
  form,
  onEditNote,
  onSaveNote,
  onCancelNote,
  onToggleHidden,
}: ApplicantRowProps) {
  const categoryInfo = applicant.categoryInfo

  return (
    <TableRow
      key={applicant._id}
      className={`bg-white border-b border-gray-200 ${categoryInfo.color} hover:bg-gray-50 transition-colors ${
        applicant.isHidden ? "opacity-60" : ""
      }`}
    >
      <TableCell className="font-medium text-gray-800">
        <div className="flex items-center">
          {applicant.firstName} {applicant.lastName}
          {applicant.isHidden && <EyeOff className="ml-2 h-4 w-4 text-gray-400" />}
        </div>
      </TableCell>
      <TableCell className="text-gray-600">{applicant.email}</TableCell>
      <TableCell>
        {applicant.qualified ? <CheckCircle className="text-green-500" /> : <XCircle className="text-red-500" />}
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-2">
          {categoryInfo.icon}
          <Badge className={categoryInfo.badge}>{applicant.category}</Badge>
        </div>
      </TableCell>
      <NoteCell
        applicant={applicant}
        isEditing={isEditing}
        form={form}
        onEditNote={onEditNote}
        onSaveNote={onSaveNote}
        onCancelNote={onCancelNote}
      />
      <TableCell>
        <PresignedLink hiringCampaignId={hiringCampaignId} resumeKey={applicant.resumeUrl ?? ""}>
          <Button
            size="sm"
            variant="outline"
            className="flex items-center bg-white text-gray-800 hover:bg-gray-100 border-gray-300"
          >
            <FileText className="h-4 w-4 mr-1" />
            View
          </Button>
        </PresignedLink>
      </TableCell>
      <TableCell>
        <Button
          size="sm"
          asChild
          variant={applicant.category === "SuperCandidate" ? "default" : "outline"}
          className={
            applicant.category === "SuperCandidate"
              ? "bg-green-600 hover:bg-green-700 text-white"
              : "bg-gray-800 hover:bg-gray-700 text-white"
          }
        >
          <Link 
            to={'/allCampaigns/$hiringCampId/summary/$applicationId'}
            from={'/allCampaigns/$hiringCampId'}
            params={{applicationId:applicant._id, hiringCampId: applicant.hiringCampaignId}}>
            Go to profile
          </Link>
        </Button>
      </TableCell>
      <TableCell>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onToggleHidden(applicant.case?._id??'', applicant.isHidden)}
          className={
            applicant.isHidden
              ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
              : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
          }
        >
          {applicant.isHidden ? (
            <>
              <Eye className="h-4 w-4 mr-1" />
              Show
            </>
          ) : (
            <>
              <EyeOff className="h-4 w-4 mr-1" />
              Hide
            </>
          )}
        </Button>
      </TableCell>
    </TableRow>
  )
}
